import React from 'react';
import { Clock, User, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ProjectStage, ProjectStatus } from '../../types';

interface StageHistoryProps {
  stages: ProjectStage[];
  className?: string;
}

const STATUS_LABELS: Record<ProjectStatus, string> = {
  [ProjectStatus.A_PREPARER]: 'À préparer',
  [ProjectStatus.SOUMIS]: 'Soumis',
  [ProjectStatus.ACCORD_PROVISOIRE]: 'Accord provisoire',
  [ProjectStatus.VALIDE]: 'Validé',
  [ProjectStatus.DEBLOCAGE_FONDS]: 'Déblocage fonds',
  [ProjectStatus.EN_COURS]: 'En cours',
  [ProjectStatus.VERIFICATION_FINALE]: 'Vérification finale',
  [ProjectStatus.TERMINE]: 'Terminé',
  [ProjectStatus.REJETE]: 'Rejeté',
  [ProjectStatus.SUSPENDU]: 'Suspendu'
};

export const StageHistory: React.FC<StageHistoryProps> = ({
  stages,
  className = ''
}) => {
  // Sort stages by reached_at descending (most recent first)
  const sortedStages = [...stages].sort((a, b) => 
    new Date(b.reached_at).getTime() - new Date(a.reached_at).getTime()
  );

  const getStatusColor = (status: ProjectStatus) => {
    switch (status) {
      case ProjectStatus.TERMINE:
        return 'bg-green-100 text-green-800 border-green-200';
      case ProjectStatus.REJETE:
        return 'bg-red-100 text-red-800 border-red-200';
      case ProjectStatus.SUSPENDU:
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case ProjectStatus.EN_COURS:
      case ProjectStatus.VERIFICATION_FINALE:
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case ProjectStatus.VALIDE:
      case ProjectStatus.DEBLOCAGE_FONDS:
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (stages.length === 0) {
    return (
      <div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-6 ${className}`}>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Historique des étapes</h3>
        <div className="text-center py-8 text-gray-500">
          <Clock className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <p>Aucun changement de statut enregistré</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Historique des étapes</h3>
      
      <div className="space-y-4">
        {sortedStages.map((stage, index) => (
          <div key={stage.id} className="relative">
            {/* Timeline connector */}
            {index < sortedStages.length - 1 && (
              <div className="absolute left-6 top-12 w-0.5 h-8 bg-gray-200" />
            )}
            
            <div className="flex items-start space-x-4">
              {/* Timeline dot */}
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-white" />
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(stage.status)}`}>
                      {STATUS_LABELS[stage.status]}
                    </span>
                    <span className="text-sm text-gray-500">
                      {format(new Date(stage.reached_at), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                    </span>
                  </div>
                  
                  {stage.set_by_name && (
                    <div className="flex items-center space-x-2 text-sm text-gray-600 mb-2">
                      <User className="w-4 h-4" />
                      <span>Mis à jour par {stage.set_by_name}</span>
                    </div>
                  )}
                  
                  {stage.note && (
                    <div className="flex items-start space-x-2 text-sm text-gray-700">
                      <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium mb-1">Note :</p>
                        <p className="italic">"{stage.note}"</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};